/**
 * Configuration the browser reads at **run time**, not build time.
 *
 * Vite inlines `import.meta.env.VITE_*` into the bundle, which is fine for a dev server
 * and wrong for a container image: the Entra client id and the API base URL would be
 * frozen at `docker build`, so the image could not be promoted from dev to staging to
 * production. You would have to rebuild per environment, and the artifact that passed the
 * tests would never be the artifact that ran.
 *
 * So `index.html` loads `/runtime-config.js` before the app bundle, and Kubernetes mounts
 * a ConfigMap over that one file per namespace. One image, three environments.
 *
 * The build-time value stays as the fallback, which is what makes `pnpm dev` and
 * `vite preview` keep working from the root `.env` with no container in sight.
 */

declare global {
  interface Window {
    __PORTAL_CONFIG__?: Readonly<Record<string, string | undefined>>;
  }
}

const runtime: Readonly<Record<string, string | undefined>> =
  typeof window === 'undefined' ? {} : (window.__PORTAL_CONFIG__ ?? {});

/**
 * Resolves one setting: the mounted runtime value first, then the value Vite inlined.
 *
 * `buildTimeValue` is passed in by the caller as a literal `import.meta.env['VITE_X']`
 * rather than looked up dynamically here. Vite's static replacement is what makes that
 * work, and a dynamic index into `import.meta.env` is exactly the kind of thing a future
 * bundler change breaks silently.
 *
 * Blank counts as unset, matching the API's `withoutBlanks`: `.env.example` ships every
 * key with an empty value as a prompt, and a ConfigMap key left empty means the same
 * thing — "not configured" — not "configured as the empty string".
 */
export function configValue(key: string, buildTimeValue: unknown): string | undefined {
  const mounted = runtime[key];
  if (typeof mounted === 'string' && mounted.trim() !== '') return mounted;
  if (typeof buildTimeValue === 'string' && buildTimeValue.trim() !== '') return buildTimeValue;
  return undefined;
}

/** True when a runtime config file was actually mounted, for the diagnostic on the sign-in screen. */
export const runtimeConfigPresent = Object.keys(runtime).length > 0;
